<script lang="ts">
/** Cache for async components to prevent remounting on every form update */
const asyncComponentCache = new Map<string, Component>();

export default {
    inheritAttrs: false
};
</script>

<script setup lang="ts">
import { Time } from '@internationalized/date';
import { CheckIcon, Loader2, RotateCcw, Sparkles, X } from '@lucide/vue';
import { refDebounced } from '@vueuse/core';
import { computed, defineAsyncComponent, inject, ref, watch, type Component, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Badge } from '@/components/ui/badge/index';
import { Button } from '@/components/ui/button/index';
import { Checkbox } from '@/components/ui/checkbox/index';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command/index';
import { Input } from '@/components/ui/input/index';
import { Label } from '@/components/ui/label/index';
import { NumberField, NumberFieldInput } from '@/components/ui/number-field/index';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/index';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select/index';
import { Slider } from '@/components/ui/slider/index';
import { Switch } from '@/components/ui/switch/index';
import { Textarea } from '@/components/ui/textarea/index';
import { TimeField, TimeFieldInput } from '@/components/ui/time-field/index';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group/index';
import { cn } from "@/lib/utils";
import { DatePicker } from '@ja/core-ui/composites/date-picker/index';
import { TimePicker } from '@ja/core-ui/composites/time-picker/index';
import { parseDate, toLocalDateOnly } from '@ja/core-ui/lib/date';

import { useUniqueCheck } from '../../composables/useUniqueCheck';
import { useViewerSheet } from '../../composables/useViewerSheet';
import { getFieldType, hasFieldType } from '../../services/fieldTypeRegistry';
import type { FieldConfig } from '../../types';

const ModelSelectField = defineAsyncComponent(() => import('./ModelSelectField.vue'));
const DurationInput = defineAsyncComponent(() => import('../DurationInput.vue'));

const props = defineProps<{
    field: FieldConfig;
    modelValue: any;
    error?: string;
    trField: (key: string) => string;
    translationPrefix?: string | null;
    /** Optional: The full item being edited, passed to relation fields for preloading labels */
    item?: Record<string, unknown>;
    /** Model name for unique validation API calls */
    model?: string;
    /** Item ID for ignoring current record in unique checks (edit mode) */
    itemId?: string | number | null;
    /** Whether this field is currently auto-generating its value */
    isAutoGenerating?: boolean;
    /** Whether the user has manually edited this auto-generated field */
    isManuallyEdited?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: any): void;
    (e: 'manualEdit', key: string): void;
    (e: 'resetAutoGenerate', key: string): void;
}>();

// =========================================================================
// Auto-generate tracking
// =========================================================================

/** Whether this field has auto-generate config */
const hasAutoGenerate = computed(() => !!props.field.autoGenerate);

/** Whether the auto-generate indicator should show (active auto-generation) */
const showAutoGenerateActive = computed(() =>
    hasAutoGenerate.value && props.isAutoGenerating && !props.isManuallyEdited,
);

/** Whether the regenerate button should show (user manually edited) */
const showRegenerateButton = computed(() =>
    hasAutoGenerate.value && props.isManuallyEdited,
);

const { t, te, locale } = useI18n();

/** BCP 47 tag for DatePicker / calendar (aligns with vue-i18n active locale) */
const datePickerLocale = computed(() => {
    const l = String(locale.value ?? 'en');
    if (l.startsWith('fr')) {
        return 'fr-CA';
    }
    if (l.startsWith('en')) {
        return 'en-US';
    }
    return l.includes('-') ? l : `${l}-US`;
});

// Inject highlighted fields state from parent (CrudEditor)
// Map from field key to { actorName }
const highlightedFields = inject<Ref<Map<string, { actorName: string }>>>('highlightedFields', ref(new Map()));

/** Get highlight info for this field (includes actor name) */
const highlightInfo = computed(() => highlightedFields.value.get(props.field.key));

/** Check if this field is currently highlighted (recently updated by another user) */
const isHighlighted = computed(() => !!highlightInfo.value);

// =========================================================================
// Unique validation
// =========================================================================

/** Track local value for debouncing unique checks */
const localValueForUnique = ref<string>('');

/** Update local value when modelValue changes */
watch(
    () => props.modelValue,
    (newVal) => {
        localValueForUnique.value = newVal != null ? String(newVal) : '';
    },
    { immediate: true }
);

/** Debounced value for unique check API calls */
const debouncedValueForUnique = refDebounced(
    localValueForUnique,
    props.field.unique?.debounce ?? 400
);

/** Unique check composable */
const uniqueConfig = computed(() => props.field.unique);
const uniqueColumn = computed(() => uniqueConfig.value?.column ?? props.field.key);

const { isUnique, isChecking: isCheckingUnique } = useUniqueCheck({
    model: () => props.model,
    field: uniqueColumn,
    value: debouncedValueForUnique,
    ignoreId: () => props.itemId,
    ignoreColumn: () => uniqueConfig.value?.ignoreColumn ?? 'id',
    enabled: () => !!uniqueConfig.value && !!props.model
});

/** Combined error message including unique validation */
const displayError = computed(() => {
    // Backend error takes priority
    if (props.error) return props.error;

    // Show unique error only if we have a value and it's not unique
    if (uniqueConfig.value && !isUnique.value && debouncedValueForUnique.value) {
        // Try custom message first, then translation, then fallback
        if (uniqueConfig.value.message && te(uniqueConfig.value.message)) {
            return t(uniqueConfig.value.message);
        }
        // Try field-specific translation: crud.validation.unique.{fieldKey}
        const fieldKey = `crud.validation.unique.${props.field.key}`;
        if (te(fieldKey)) {
            return t(fieldKey);
        }
        // Generic fallback
        return t('crud.validation.unique', { field: props.trField(props.field.key) });
    }

    return undefined;
});

/**
 * Normalize options to array of {value, label} objects.
 * Supports:
 * - Simple values: [true, false, "active", "inactive"]
 * - Objects with value: [{value: true}, {value: false}]
 * - Objects with value+label (legacy): [{value: true, label: "Yes"}]
 */
const normalizedOptions = computed(() => {
    if (!props.field.options) return [];

    return props.field.options.map((opt: any) => {
        // If it's a simple value (not an object), wrap it
        if (typeof opt !== 'object' || opt === null) {
            return { value: opt, label: null };
        }
        // It's an object with value (and maybe label)
        return { value: opt.value, label: opt.label ?? null };
    });
});

/**
 * Get the translated label for an option.
 * Try: models.{prefix}.options.{fieldKey}.{value}
 * Fallback to explicit label, then humanized value.
 */
function getOptionLabel(optValue: any, explicitLabel?: string | null): string {
    const strValue = String(optValue);

    // 1. Try translation key: models.{prefix}.options.{fieldKey}.{value}
    if (props.translationPrefix) {
        const key = `models.${props.translationPrefix}.options.${props.field.key}.${strValue}`;
        if (te(key)) {
            return t(key);
        }
        // Shared badge labels (priority, status, channels, etc.)
        const badgeKey = `models.${props.translationPrefix}.badges.${strValue}`;
        if (te(badgeKey)) {
            return t(badgeKey);
        }
    }

    // 2. If explicit label provided and it looks like a translation key, try it
    if (explicitLabel && explicitLabel.includes('.') && te(explicitLabel)) {
        return t(explicitLabel);
    }

    // 3. If explicit label provided and it's not a key, use it
    if (explicitLabel) {
        return explicitLabel;
    }

    // 4. Fallback: humanize boolean or return value
    if (optValue === true || strValue === 'true') return t('crud.yes') || 'Yes';
    if (optValue === false || strValue === 'false') return t('crud.no') || 'No';

    return strValue;
}

const value = computed({
    get: () => {
        // For multiselect fields, ensure we return an array
        if (props.field.multiple && !Array.isArray(props.modelValue)) {
            return props.modelValue != null ? [props.modelValue] : [];
        }
        return props.modelValue;
    },
    set: (val) => {
        // Track manual edits on auto-generated fields
        if (hasAutoGenerate.value) {
            emit('manualEdit', props.field.key);
        }
        emit('update:modelValue', val);
    },
});

/** Special computed for components that require string values (Select, ToggleGroup) */
const stringValue = computed({
    get: () => {
        if (value.value === null || value.value === undefined) return undefined;
        return String(value.value);
    },
    set: (val) => {
        // Try to preserve original type if possible
        if (typeof props.modelValue === 'boolean') {
            value.value = val === 'true' || val === '1';
        } else if (typeof props.modelValue === 'number') {
            value.value = Number(val);
        } else {
            value.value = val;
        }
    },
});

/** Computed for Switch state */
const switchChecked = computed({
    get: () => {
        const val = value.value;
        const trueVal = props.field.trueValue;

        if (trueVal !== undefined && trueVal !== null) {
            return val == trueVal;
        }

        // Default truthy check for common boolean representations
        return val === true || val === 1 || val === '1' || val === 'true';
    },
    set: (val: boolean) => {
        const trueVal = props.field.trueValue;
        const falseVal = props.field.falseValue;

        if (val) {
            value.value = trueVal !== undefined && trueVal !== null ? trueVal : true;
        } else {
            value.value = falseVal !== undefined && falseVal !== null ? falseVal : false;
        }
    },
});

/** Computed for JSON fields (serialize/deserialize between object and pretty string) */
const jsonString = computed({
    get: () => {
        const val = value.value;
        if (val === null || val === undefined || val === '') return '';
        if (typeof val === 'string') return val;
        try {
            return JSON.stringify(val, null, 2);
        } catch {
            return String(val);
        }
    },
    set: (val: string) => {
        if (!val.trim()) {
            value.value = null;
            return;
        }
        try {
            value.value = JSON.parse(val);
        } catch {
            // Keep the raw string so the user can keep editing
            value.value = val;
        }
    },
});

/** Computed for TimeField (expects Time object or null) */
const timeValue = computed({
    get: () => {
        if (!value.value) return undefined;
        try {
            // Expecting format HH:mm or HH:mm:ss
            const parts = String(value.value).split(':');
            if (parts.length >= 2) {
                return new Time(Number(parts[0]), Number(parts[1]), parts[2] ? Number(parts[2]) : 0);
            }
        } catch (e) {
            console.error('Invalid time format', value.value);
        }
        return undefined;
    },
    set: (val) => {
        if (!val) {
            value.value = null;
            return;
        }
        // Convert Time object back to string HH:mm:ss for backend
        value.value = `${String(val.hour).padStart(2, '0')}:${String(val.minute).padStart(2, '0')}:${String(val.second).padStart(2, '0')}`;
    },
});

/**
 * Computed for DatePicker (expects Date object or undefined).
 * Handles conversion between string date format (from backend) and Date object.
 */
const dateValue = computed({
    get: () => parseDate(value.value as string | null | undefined) ?? undefined,
    set: (val: Date | undefined) => {
        value.value = val ? toLocalDateOnly(val) : null;
    },
});

/**
 * Parsed datetime base for the datetime field. Both halves of the split picker
 * (date + time) read from this. Years outside 1..9999 are dropped so the date
 * and time halves stay in lockstep with DatePicker's own range gate.
 */
const DATETIME_MIN_YEAR = 1;
const DATETIME_MAX_YEAR = 9999;

const datetimeBase = computed<Date | undefined>(() => {
    const parsed = parseDate(value.value as string | number | null | undefined);
    if (!parsed) return undefined;
    const year = parsed.getFullYear();
    if (!Number.isFinite(year) || year < DATETIME_MIN_YEAR || year > DATETIME_MAX_YEAR) {
        return undefined;
    }
    return parsed;
});

/**
 * Date half of the datetime field. Updating the date preserves the existing
 * time component; clearing the date wipes the whole field.
 */
const datetimeDateValue = computed<Date | undefined>({
    get: () => datetimeBase.value,
    set: (val: Date | undefined) => {
        if (!val) {
            value.value = null;
            return;
        }
        const next = datetimeBase.value ? new Date(datetimeBase.value) : new Date();
        next.setFullYear(val.getFullYear(), val.getMonth(), val.getDate());
        if (!datetimeBase.value) {
            next.setHours(0, 0, 0, 0);
        }
        value.value = next.toISOString();
    },
});

/**
 * Time half of the datetime field. Only writes when a date already exists —
 * a time without a date has no meaningful ISO encoding. Clearing the time
 * with a date set rewinds to 00:00:00 on that date so the picker stays in
 * sync with what the user sees instead of silently snapping back.
 */
const datetimeTimeValue = computed<Date | undefined>({
    get: () => datetimeBase.value,
    set: (val: Date | undefined) => {
        const base = datetimeBase.value;
        if (!base) return;
        const next = new Date(base);
        if (val) {
            next.setHours(val.getHours(), val.getMinutes(), val.getSeconds(), 0);
        } else {
            next.setHours(0, 0, 0, 0);
        }
        value.value = next.toISOString();
    },
});

/**
 * True when value.value holds a parseable date that the picker rejected
 * (year out of 1..9999). Used to surface the silent gate to the user.
 */
const datetimeOutOfRange = computed<boolean>(() => {
    if (value.value === null || value.value === undefined || value.value === '') return false;
    const parsed = parseDate(value.value as string | number | null | undefined);
    if (!parsed) return false;
    const year = parsed.getFullYear();
    return !Number.isFinite(year) || year < DATETIME_MIN_YEAR || year > DATETIME_MAX_YEAR;
});

/** Get field input type for HTML input */
function getInputType(field: FieldConfig): string {
    switch (field.type) {
        case 'email':
            return 'email';
        case 'password':
            return 'password';
        case 'number':
        case 'currency':
            return 'number';
        default:
            return 'text';
    }
}

/** Get grid column span class */
function getSpanClass(field: FieldConfig): string {
    const span = field.colspan ?? 1;
    if (span >= 4) return 'col-span-full';
    if (span === 3) return 'md:col-span-3';
    if (span === 2) return 'md:col-span-2';
    return '';
}

/** Check if a value is selected in multiselect */
function isValueSelected(optionValue: string | number): boolean {
    if (!Array.isArray(value.value)) return false;
    return value.value.some((v) => String(v) === String(optionValue));
}

/** Toggle a value in multiselect array */
function toggleMultipleValue(optionValue: string | number): void {
    const currentArray = Array.isArray(value.value) ? [...value.value] : [];
    const stringValue = String(optionValue);
    const index = currentArray.findIndex((v) => String(v) === stringValue);
    
    if (index >= 0) {
        // Remove if already selected
        currentArray.splice(index, 1);
    } else {
        // Add if not selected
        currentArray.push(optionValue);
    }
    
    value.value = currentArray.length > 0 ? currentArray : [];
}

/** Media field: placeholder line from model or crud fallback */
const mediaPlaceholderLine = computed(() => {
    if (props.translationPrefix) {
        const key = `models.${props.translationPrefix}.placeholders.media_placeholder`;
        if (te(key)) {
            return t(key);
        }
    }
    if (te('crud.media_placeholder')) {
        return t('crud.media_placeholder');
    }
    return 'Media upload component placeholder';
});

/** Media field: integration note from model translations */
const mediaIntegrationNote = computed(() => {
    if (props.translationPrefix) {
        const key = `models.${props.translationPrefix}.media.requires_spatie`;
        if (te(key)) {
            return t(key);
        }
    }
    return '(Requires Spatie Media Library integration)';
});

/** Current index for enumSlider (priority-style stepped values) */
const enumSliderIndex = computed(() => {
    const vals = normalizedOptions.value.map((o) => String(o.value));
    const cur = stringValue.value ?? '';
    let idx = vals.indexOf(String(cur));
    if (idx < 0) {
        idx = 0;
    }
    return idx;
});

const enumSliderMax = computed(() => Math.max(0, normalizedOptions.value.length - 1));

const enumSliderCurrentLabel = computed(() => {
    const opt = normalizedOptions.value[enumSliderIndex.value];
    if (!opt) {
        return '';
    }
    return getOptionLabel(opt.value, opt.label);
});

function onEnumSliderUpdate(raw: number[] | undefined): void {
    const idx = Math.min(Math.max(0, raw?.[0] ?? 0), enumSliderMax.value);
    const opt = normalizedOptions.value[idx];
    if (opt) {
        stringValue.value = String(opt.value);
    }
}

/** Remove a value from multiselect array */
function removeMultipleValue(optionValue: string | number): void {
    const currentArray = Array.isArray(value.value) ? [...value.value] : [];
    const stringValue = String(optionValue);
    const index = currentArray.findIndex((v) => String(v) === stringValue);
    
    if (index >= 0) {
        currentArray.splice(index, 1);
        value.value = currentArray.length > 0 ? currentArray : [];
    }
}

/** Built-in field types handled by this component */
const builtInTypes = [
    'text',
    'email',
    'password',
    'number',
    'currency',
    'date',
    'datetime',
    'textarea',
    'relation',
    'select',
    'checkbox',
    'switch',
    'toggleGroup',
    'enumSlider',
    'time',
    'media',
    'json',
    'breakpoint',
    'weeklyAvailability',
    'badge',
    'linkedRelation',
];

/** Check if field type is custom (registered via fieldTypeRegistry) */
const isCustomFieldType = computed(() => {
    return !builtInTypes.includes(props.field.type) && hasFieldType(props.field.type);
});

/** Sheet target (model + id) for `linkedRelation` fields. */
const linkedRelationSheet = useViewerSheet();
const linkedRelationTarget = computed<{ model: string; id: string } | null>(() => {
    if (props.field.type !== 'linkedRelation') return null;
    const meta = (props.field.metadata ?? {}) as { modelKey?: string; idKey?: string };
    if (!meta.modelKey || !meta.idKey || !props.item) return null;
    const id = props.item[meta.idKey];
    if (id === null || id === undefined || id === '') return null;
    return { model: meta.modelKey, id: String(id) };
});

function openLinkedRelation(): void {
    if (!linkedRelationTarget.value) return;
    linkedRelationSheet.openViewerSheet(linkedRelationTarget.value.model, linkedRelationTarget.value.id);
}

/** Get the custom field component if registered */
const customFieldComponent = computed<Component | null>(() => {
    const fieldType = props.field.type;
    if (!isCustomFieldType.value) return null;

    // Return from cache if already resolved
    if (asyncComponentCache.has(fieldType)) {
        return asyncComponentCache.get(fieldType)!;
    }

    const component = getFieldType(fieldType);
    if (!component) return null;

    // If it's an async component (loader function), wrap it with defineAsyncComponent
    if (typeof component === 'function') {
        const asyncComp = defineAsyncComponent(component as any);
        asyncComponentCache.set(fieldType, asyncComp);
        return asyncComp;
    }

    // If it's already a component module with default export
    const resolvedComp = (component as { default: Component }).default ?? null;
    if (resolvedComp) {
        asyncComponentCache.set(fieldType, resolvedComp);
    }
    return resolvedComp;
});
</script>

<template>
    <!-- Breakpoint -->
    <div v-if="field.isBreakpoint || field.type === 'breakpoint'" class="col-span-full h-0 opacity-0"></div>

    <div v-else class="relative w-full transition-all duration-300" :class="[getSpanClass(field), isHighlighted && 'rounded-lg border border-blue-300 bg-blue-50/70 p-2 dark:border-blue-700 dark:bg-blue-950/40']">
        <!-- Real-time update indicator -->
        <div
            v-if="isHighlighted && highlightInfo"
            class="mb-1.5 inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400"
        >
            <span class="relative flex size-2">
                <span class="absolute inline-flex size-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                <span class="relative inline-flex size-2 rounded-full bg-blue-500"></span>
            </span>
            {{ t('crud.realtime.updated_by', { user: highlightInfo.actorName }) }}
        </div>

        <!-- Text, Email, Password inputs -->
        <template v-if="['text', 'email', 'password'].includes(field.type)">
            <div class="flex items-center gap-1.5">
                <Label :for="field.key">
                    {{ trField(field.key) }}
                    <span v-if="field.required" class="text-destructive">*</span>
                </Label>
                <Sparkles v-if="showAutoGenerateActive" class="text-muted-foreground size-3.5" />
                <button
                    v-if="showRegenerateButton"
                    type="button"
                    class="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 text-xs transition-colors"
                    :title="t('crud.auto_generate.regenerate')"
                    @click="emit('resetAutoGenerate', field.key)"
                >
                    <RotateCcw class="size-3" />
                </button>
            </div>
            <!-- Use Textarea with markdown support for text fields when markdown is enabled -->
            <div v-if="field.type === 'text' && (field.markdown || (field as any).is_markdown)" class="relative mt-1.5">
                <Textarea
                    :id="field.key"
                    v-model="value"
                    :placeholder="field.placeholder"
                    :disabled="field.disabled"
                    :required="field.required"
                    :markdown="field.markdown || (field as any).is_markdown"
                    :aria-invalid="!!displayError"
                />
                <!-- Unique check loading indicator -->
                <div v-if="field.unique && isCheckingUnique" class="absolute top-2 right-2 flex items-center">
                    <Loader2 class="text-muted-foreground size-4 animate-spin" />
                </div>
            </div>
            <!-- Regular Input for non-markdown text fields and other input types -->
            <div v-else class="relative mt-1.5">
                <Input
                    :id="field.key"
                    v-model="value"
                    :type="getInputType(field)"
                    :placeholder="field.placeholder"
                    :disabled="field.disabled"
                    :required="field.required"
                    :aria-invalid="!!displayError"
                    :class="{ 'pr-8': field.unique }"
                />
                <!-- Unique check loading indicator -->
                <div v-if="field.unique && isCheckingUnique" class="absolute inset-y-0 right-0 flex items-center pr-2.5">
                    <Loader2 class="text-muted-foreground size-4 animate-spin" />
                </div>
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="displayError" class="text-destructive mt-1 text-sm">
                {{ displayError }}
            </p>
        </template>

        <!-- Number / Currency inputs (uses reka-ui NumberField to preserve decimal input) -->
        <template v-else-if="['number', 'currency'].includes(field.type)">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <NumberField
                :id="field.key"
                v-model="value"
                :min="(field.metadata?.min ?? (field as any).min) as number | undefined"
                :max="(field.metadata?.max ?? (field as any).max) as number | undefined"
                :step="(field.metadata?.step ?? (field as any).step) as number | undefined"
                :disabled="field.disabled"
                :format-options="field.type === 'currency' ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : undefined"
                class="mt-1.5"
            >
                <NumberFieldInput />
            </NumberField>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="displayError" class="text-destructive mt-1 text-sm">
                {{ displayError }}
            </p>
        </template>

        <!-- Duration input: integer minutes with a display-mode toggle -->
        <template v-else-if="field.type === 'duration'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <div class="mt-1.5">
                <DurationInput
                    :id="field.key"
                    v-model="value"
                    :disabled="field.disabled"
                    :default-mode="(field.metadata?.defaultMode as 'hhmm' | 'decimal' | 'minutes' | undefined) ?? 'hhmm'"
                />
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="displayError" class="text-destructive mt-1 text-sm">
                {{ displayError }}
            </p>
        </template>

        <!-- Date Picker -->
        <template v-else-if="field.type === 'date'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <div class="mt-1.5">
                <DatePicker
                    v-model="dateValue"
                    :locale="datePickerLocale"
                    :placeholder="field.placeholder ?? t('crud.select_date')"
                    :disabled="field.disabled"
                    layout="month-and-year"
                    class="w-full"
                />
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- DateTime Picker (date + time side-by-side) -->
        <template v-else-if="field.type === 'datetime'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <div class="mt-1.5 flex flex-wrap items-center gap-2">
                <DatePicker
                    v-model="datetimeDateValue"
                    :locale="datePickerLocale"
                    :placeholder="field.placeholder ?? t('crud.select_datetime')"
                    :disabled="field.disabled"
                    layout="month-and-year"
                    class="w-full sm:w-[280px]"
                />
                <TimePicker
                    v-model="datetimeTimeValue"
                    :locale="datePickerLocale"
                    :disabled="field.disabled || !datetimeBase"
                />
            </div>
            <p v-if="datetimeOutOfRange" class="text-amber-600 mt-1 text-sm dark:text-amber-400">
                {{ t('crud.datetime_out_of_range') }}
            </p>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Textarea -->
        <template v-else-if="field.type === 'textarea'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <Textarea
                :id="field.key"
                v-model="value"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :required="field.required"
                :markdown="field.markdown || (field as any).is_markdown"
                :aria-invalid="!!error"
                class="mt-1.5"
            />
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- JSON Field -->
        <template v-else-if="field.type === 'json'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <Textarea
                :id="field.key"
                v-model="jsonString"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :required="field.required"
                :aria-invalid="!!error"
                class="mt-1.5 font-mono text-xs"
                rows="12"
            />
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Model Select (relation type with relationModel) -->
        <template v-else-if="field.type === 'relation' && (field.relationModel || (field as any).relation_model)">
            <ModelSelectField
                :field="field"
                :model-value="value"
                :error="error"
                :tr-field="trField"
                :translation-prefix="translationPrefix"
                :item="item"
                @update:model-value="value = $event"
            />
        </template>

        <!-- Select (static options) -->
        <template v-else-if="field.type === 'select' || field.type === 'relation'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            
            <template v-if="field.multiple">
                <!-- Multi-select using Popover + Command for proper dropdown -->
                <Popover>
                    <PopoverTrigger as-child>
                        <Button
                            variant="outline"
                            role="combobox"
                            :disabled="field.disabled"
                            class="mt-1.5 w-full justify-between font-normal"
                        >
                            <span class="truncate">
                                <template v-if="Array.isArray(value) && value.length > 0">
                                    {{ value.length }} {{ value.length === 1 ? t('crud.item_selected') : t('crud.items_selected') }}
                                </template>
                                <template v-else>
                                    {{ field.placeholder ?? t('crud.select_placeholder') }}
                                </template>
                            </span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent class="w-[--reka-popover-trigger-width] p-0" align="start">
                        <Command>
                            <CommandList>
                                <CommandEmpty>{{ t('crud.no_results') }}</CommandEmpty>
                                <CommandGroup>
                                    <CommandItem
                                        v-for="option in normalizedOptions"
                                        :key="option.value"
                                        :value="String(option.value)"
                                        @select="toggleMultipleValue(option.value)"
                                    >
                                        <div
                                            :class="
                                                cn(
                                                    'border-primary mr-2 flex size-4 items-center justify-center rounded-sm border',
                                                    isValueSelected(option.value) ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                                                )
                                            "
                                        >
                                            <CheckIcon class="size-4" />
                                        </div>
                                        <span>{{ getOptionLabel(option.value, option.label) }}</span>
                                    </CommandItem>
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                <!-- Display selected values as badges -->
                <div v-if="Array.isArray(value) && value.length > 0" class="mt-2 flex flex-wrap gap-2">
                    <Badge
                        v-for="selectedValue in value"
                        :key="selectedValue"
                        variant="secondary"
                        class="pr-1"
                    >
                        {{ getOptionLabel(selectedValue, normalizedOptions.find((o) => String(o.value) === String(selectedValue))?.label) }}
                        <button
                            type="button"
                            class="ml-1 rounded-full hover:bg-secondary-foreground/20"
                            @click="removeMultipleValue(selectedValue)"
                        >
                            <X class="size-3" />
                        </button>
                    </Badge>
                </div>
            </template>
            <template v-else>
                <Select v-model="value" :disabled="field.disabled">
                    <SelectTrigger class="mt-1.5">
                        <SelectValue :placeholder="field.placeholder ?? t('crud.select_placeholder')" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem v-for="option in normalizedOptions" :key="option.value" :value="String(option.value)">
                            {{ getOptionLabel(option.value, option.label) }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </template>
            
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Checkbox -->
        <template v-else-if="field.type === 'checkbox'">
            <div class="flex items-center space-x-2">
                <Checkbox :id="field.key" :checked="!!value" @update:checked="value = $event" :disabled="field.disabled" />
                <Label :for="field.key" class="cursor-pointer">
                    {{ trField(field.key) }}
                </Label>
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
        </template>

        <!-- Badge: read-only display for enum / boolean values -->
        <template v-else-if="field.type === 'badge'">
            <Label>{{ trField(field.key) }}</Label>
            <div class="mt-1.5">
                <Badge
                    v-if="value !== null && value !== undefined && value !== ''"
                    variant="secondary"
                    class="text-xs"
                >
                    {{ getOptionLabel(value) }}
                </Badge>
                <span v-else class="text-muted-foreground text-sm">—</span>
            </div>
        </template>

        <!-- Switch -->
        <template v-else-if="field.type === 'switch'">
            <div class="flex flex-col">
                <Label :for="field.key" class="cursor-pointer">{{ trField(field.key) }}</Label>
                <p v-if="field.description" class="text-muted-foreground text-sm">
                    {{ field.description }}
                </p>
                <div class="mt-1.5 flex justify-start">
                    <Switch :id="field.key" v-model="switchChecked" :disabled="field.disabled" />
                </div>
            </div>
        </template>

        <!-- Discrete enum as stepped slider (e.g. communication priority) -->
        <template v-else-if="field.type === 'enumSlider'">
            <div class="flex items-center justify-between gap-2">
                <Label :for="field.key" class="min-w-0 flex-1 leading-snug">
                    {{ trField(field.key) }}
                    <span v-if="field.required" class="text-destructive">*</span>
                </Label>
                <Badge variant="secondary" class="shrink-0 text-xs font-medium">
                    {{ enumSliderCurrentLabel }}
                </Badge>
            </div>
            <div class="mt-3 w-full">
                <Slider
                    :id="field.key"
                    :model-value="[enumSliderIndex]"
                    :min="0"
                    :max="enumSliderMax"
                    :step="1"
                    :disabled="field.disabled"
                    class="crud-enum-slider w-full"
                    @update:model-value="onEnumSliderUpdate"
                />
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="displayError" class="text-destructive mt-1 text-sm">
                {{ displayError }}
            </p>
        </template>

        <!-- Toggle Group -->
        <template v-else-if="field.type === 'toggleGroup'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <ToggleGroup :type="field.multiple ? 'multiple' : 'single'" v-model="value" :disabled="field.disabled" class="mt-1.5 justify-start">
                <ToggleGroupItem
                    v-for="option in normalizedOptions"
                    :key="option.value"
                    :value="String(option.value)"
                    variant="outline"
                    class="flex-1"
                >
                    {{ getOptionLabel(option.value, option.label) }}
                </ToggleGroupItem>
            </ToggleGroup>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Time Field -->
        <template v-else-if="field.type === 'time'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <TimeField :id="field.key" locale="fr-CA" v-model="timeValue" :disabled="field.disabled" class="mt-1.5">
                <TimeFieldInput />
            </TimeField>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Media Field -->
        <template v-else-if="field.type === 'media'">
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <div class="mt-1.5 rounded-md border border-dashed p-4 text-center">
                <p class="text-muted-foreground text-sm">
                    {{ mediaPlaceholderLine }}
                </p>
                <p class="text-muted-foreground text-xs">
                    {{ mediaIntegrationNote }}
                </p>
            </div>
            <p v-if="field.description" class="text-muted-foreground mt-1 text-sm">
                {{ field.description }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>

        <!-- Linked relation: opens the related record in the ViewerSheet -->
        <template v-else-if="field.type === 'linkedRelation'">
            <Label :for="field.key">
                {{ trField(field.key) }}
            </Label>
            <div class="mt-1.5 text-sm">
                <button
                    v-if="linkedRelationTarget && value"
                    type="button"
                    class="text-primary cursor-pointer text-left hover:underline"
                    @click.stop="openLinkedRelation"
                >
                    {{ value }}
                </button>
                <span v-else class="text-muted-foreground">—</span>
            </div>
        </template>

        <!-- Custom Field Type (registered via fieldTypeRegistry) -->
        <template v-else-if="isCustomFieldType && customFieldComponent">
            <component
                :is="customFieldComponent"
                :field="field"
                :model-value="value"
                :error="error"
                :tr-field="trField"
                :translation-prefix="translationPrefix"
                :item="item"
                :model="model"
                :item-id="itemId"
                @update:model-value="value = $event"
            />
        </template>

        <!-- Unknown field type fallback -->
        <template v-else>
            <Label :for="field.key">
                {{ trField(field.key) }}
                <span v-if="field.required" class="text-destructive">*</span>
            </Label>
            <Input
                :id="field.key"
                v-model="value"
                type="text"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :required="field.required"
                :aria-invalid="!!error"
                class="mt-1.5"
            />
            <p class="text-muted-foreground mt-1 text-xs">
                Unknown field type: {{ field.type }}
            </p>
            <p v-if="error" class="text-destructive mt-1 text-sm">
                {{ error }}
            </p>
        </template>
    </div>
</template>

<style scoped>
/* Priority-style slider: full-track gradient; thumb indicates level */
.crud-enum-slider :deep([data-slot='slider-track']) {
    height: 0.5rem;
    border-radius: 9999px;
    background: linear-gradient(90deg, rgb(34 197 94), rgb(234 179 8), rgb(249 115 22), rgb(239 68 68));
}
.crud-enum-slider :deep([data-slot='slider-range']) {
    background: transparent !important;
}
</style>
